// src/pages/Transports.js — Admin portal transport management
import React, { useState, useEffect } from 'react';
import { get, post, patch, del } from '../utils/api';
import { Spinner, Alert, Modal, Input, Select } from '../components/Shared';

const TYPE_OPTIONS = [
  { value:'courier',   label:'Courier' },
  { value:'logistics', label:'Logistics' },
  { value:'3pl',       label:'3PL / Own Vehicle' },
];

export default function Transports() {
  const [transports, setTransports] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showAdd,    setShowAdd]    = useState(false);
  const [msg,        setMsg]        = useState(null);
  const [form,       setForm]       = useState({ name:'', type:'courier', phone:'' });
  const [saving,     setSaving]     = useState(false);
  const [delTransport, setDelTransport] = useState(null);
  const [outstationsFor, setOutstationsFor] = useState(null); // transport object, or null
  const [editTransport, setEditTransport] = useState(null); // transport object, or null
  const [editForm, setEditForm] = useState({ name:'', phone:'' });
  const [savingEdit, setSavingEdit] = useState(false);

  async function load() {
    try {
      const data = await get('/api/transports?active_only=false');
      setTransports(data.transports || []);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function addTransport(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await post('/api/transports', form);
      setMsg({ type:'success', text:`${form.name} added successfully` });
      setShowAdd(false);
      setForm({ name:'', type:'courier', phone:'' });
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally { setSaving(false); }
  }

  async function toggleActive(t) {
    try {
      await patch(`/api/transports/${t.id}`, { is_active: !t.is_active });
      setMsg({ type:'success', text:`${t.name} ${t.is_active ? 'deactivated' : 'activated'}` });
      load();
    } catch (err) { setMsg({ type:'error', text: err.message }); }
  }

  async function deleteTransport(t) {
    try {
      await del(`/api/transports/${t.id}`);
      setMsg({ type:'success', text:`${t.name} deleted` });
      setDelTransport(null);
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
      setDelTransport(null);
    }
  }

  function openEdit(t) {
    setEditTransport(t);
    setEditForm({ name: t.name || '', phone: t.phone || '' });
  }

  async function saveEdit(e) {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await patch(`/api/transports/${editTransport.id}`, { name: editForm.name, phone: editForm.phone });
      setMsg({ type:'success', text:`${editForm.name} updated successfully` });
      setEditTransport(null);
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally { setSavingEdit(false); }
  }

  // NOTE: transport-type color-coding left untouched — same categorical
  // reasoning as invoice stage colors and role colors.
  const TYPE_COLORS = {
    courier:   'bg-blue-100 text-blue-700',
    logistics: 'bg-purple-100 text-purple-700',
    '3pl':     'bg-green-100 text-green-700',
  };

  if (loading) return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-[1600px]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Transport Management</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {transports.filter(t => t.is_active).length} active courier and logistics companies
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-brand-700 text-white text-sm rounded-lg hover:bg-brand-500 font-semibold"
        >
          + Add Transport
        </button>
      </div>

      {msg && <div className="mb-4"><Alert type={msg.type} message={msg.text} onClose={() => setMsg(null)} /></div>}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              {['Name','Type','Phone','Status','Actions'].map((h,i) => (
                <th key={i} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {transports.map((t, i) => (
              <tr key={t.id} className={`border-t ${!t.is_active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{t.name}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_COLORS[t.type] || 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}>
                    {t.type}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{t.phone || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEdit(t)}
                      className="text-xs px-2 py-1 rounded font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setOutstationsFor(t)}
                      className="text-xs px-2 py-1 rounded font-medium bg-brand-700/5 text-brand-700 hover:bg-brand-700/10"
                    >
                      Outstations
                    </button>
                    <button
                      onClick={() => toggleActive(t)}
                      className={`text-xs px-2 py-1 rounded font-medium ${t.is_active ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}
                    >
                      {t.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => setDelTransport(t)}
                      className="text-xs px-2 py-1 rounded font-medium bg-red-50 text-red-600 hover:bg-red-100"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {delTransport && (
        <Modal title="Delete Transport" onClose={() => setDelTransport(null)}>
          <div className="text-center py-4">
            <p className="text-5xl mb-4">⚠️</p>
            <p className="text-gray-800 dark:text-gray-100 font-semibold text-lg mb-2">Delete {delTransport.name}?</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
              This permanently removes this transport from the system. Historical delivery records using this transport will still show the transport name.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDelTransport(null)}
                className="flex-1 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800">
                Cancel
              </button>
              <button onClick={() => deleteTransport(delTransport)}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-500">
                Yes, Delete
              </button>
            </div>
          </div>
        </Modal>
      )}

      {editTransport && (
        <Modal title={`Edit Transport — ${editTransport.name}`} onClose={() => setEditTransport(null)}>
          <form onSubmit={saveEdit}>
            <Input
              label="Company Name"
              value={editForm.name}
              onChange={e => setEditForm({...editForm, name:e.target.value})}
              required
              placeholder="e.g. BlueDart, DTDC, Own Vehicle"
            />
            <Input
              label="Phone Number"
              value={editForm.phone}
              onChange={e => setEditForm({...editForm, phone:e.target.value})}
              placeholder="e.g. 9876543210"
            />
            <div className="flex gap-3 mt-2">
              <button type="button" onClick={() => setEditTransport(null)}
                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800">
                Cancel
              </button>
              <button type="submit" disabled={savingEdit}
                className="flex-1 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-500 disabled:opacity-60">
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showAdd && (
        <Modal title="Add Transport Company" onClose={() => setShowAdd(false)}>
          <form onSubmit={addTransport}>
            <Input
              label="Company Name"
              value={form.name}
              onChange={e => setForm({...form, name:e.target.value})}
              required
              placeholder="e.g. BlueDart, DTDC, Own Vehicle"
            />
            <Select
              label="Type"
              value={form.type}
              onChange={e => setForm({...form, type:e.target.value})}
              options={TYPE_OPTIONS}
            />
            <Input
              label="Phone Number"
              value={form.phone}
              onChange={e => setForm({...form, phone:e.target.value})}
              placeholder="e.g. 9876543210"
            />
            <div className="flex gap-3 mt-2">
              <button type="button" onClick={() => setShowAdd(false)}
                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-500 disabled:opacity-60">
                {saving ? 'Adding...' : 'Add Transport'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {outstationsFor && (
        <OutstationsModal
          transport={outstationsFor}
          onClose={() => setOutstationsFor(null)}
        />
      )}
    </div>
  );
}

// Outstation history + freight rate entry for one transport. History is
// derived live from actual delivery records (grouped by station) — not
// something an admin enters. Freight rate IS admin-entered, per station.
function OutstationsModal({ transport, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStation, setEditingStation] = useState(null);
  const [rateInput, setRateInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const data = await get(`/api/transports/${transport.id}/outstations`);
      setRows(data.outstations || []);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function startEdit(row) {
    setEditingStation(row.station_code);
    setRateInput(row.freight_rate != null ? String(row.freight_rate) : '');
  }

  async function saveRate(stationCode) {
    if (!rateInput || isNaN(parseFloat(rateInput))) {
      setMsg({ type: 'error', text: 'Enter a valid rate' });
      return;
    }
    setSaving(true);
    try {
      // api.js doesn't export a put() helper, so this uses fetch directly
      // with the same auth pattern as get/post/patch/del in utils/api.js.
      const token = localStorage.getItem('dms_token');
      const res = await fetch(`/api/transports/${transport.id}/rates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ station: stationCode, freight_rate: parseFloat(rateInput) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save rate');
      setMsg({ type: 'success', text: 'Rate updated' });
      setEditingStation(null);
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={`Outstations — ${transport.name}`} onClose={onClose} width="max-w-2xl">
      {msg && <div className="mb-4"><Alert type={msg.type} message={msg.text} onClose={() => setMsg(null)} /></div>}
      {loading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : rows.length === 0 ? (
        <p className="text-center text-gray-400 dark:text-gray-500 text-sm py-10">
          No outstation deliveries recorded for this transport yet.
        </p>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                {['Station', 'Deliveries', 'Last Delivery', 'Freight Rate', ''].map((h, i) => (
                  <th key={i} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.station_code} className="border-t">
                  <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-100">
                    {r.station_name}
                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">({r.station_code})</span>
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{r.delivery_count}</td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">
                    {r.last_delivery_at ? new Date(r.last_delivery_at).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {editingStation === r.station_code ? (
                      <input
                        type="number"
                        value={rateInput}
                        onChange={e => setRateInput(e.target.value)}
                        className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm"
                        autoFocus
                      />
                    ) : (
                      <span className="text-gray-700 dark:text-gray-300">{r.freight_rate != null ? `₹${r.freight_rate}` : '—'}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {editingStation === r.station_code ? (
                      <div className="flex gap-1">
                        <button onClick={() => saveRate(r.station_code)} disabled={saving}
                          className="text-xs px-2 py-1 bg-brand-700 text-white rounded hover:bg-brand-500 disabled:opacity-60">
                          Save
                        </button>
                        <button onClick={() => setEditingStation(null)}
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(r)}
                        className="text-xs px-2 py-1 text-brand-500 hover:underline">
                        {r.freight_rate != null ? 'Edit' : 'Set rate'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
